import React, { useState } from 'react';
import { Plus, Edit2, Trash2, ChevronDown, ChevronUp, FileText, Paperclip, Download, Send, Users } from 'lucide-react';
import { exportarPDF, exportarWord, htmlAta, gerarPDFBlob } from '../utils/exportDoc';
import { toast } from '../utils/toast';
import emailjs from '@emailjs/browser';
import { carregarConfigEmail } from '../utils/emailBackup';

// Atas antigas (versões pré-multi-participante) podem ter `participantes` salvo
// como string ou objeto único em vez de array. `x || []` só protege contra
// falsy; precisa checar Array.isArray pra não quebrar `.filter`/`.map`.
const asArray = (v) => (Array.isArray(v) ? v : []);
async function enviarAtaPorEmail(ata, nomeCliente) {
  const config = carregarConfigEmail();
  if (!config.ativo || !config.serviceId || !config.publicKey) {
    return { ok: false, erro: 'Email não configurado no sistema.' };
  }
  const destinatarios = (ata.emailsDestinatarios || '').trim();
  if (!destinatarios) {
    return { ok: false, erro: 'Informe ao menos um e-mail destinatário.' };
  }

  const dataFmt = ata.data ? new Date(ata.data + 'T00:00').toLocaleDateString('pt-BR') : '—';
  const assunto = `Ata de Reunião — ${nomeCliente || 'Reunião'} — ${dataFmt}`;

  // Gera e baixa o PDF localmente (EmailJS plano gratuito não suporta anexos)
  try {
    const tituloDoc = `Ata de Reunião - ${nomeCliente || 'Reunião'} - ${dataFmt}`;
    const pdfBlob = await gerarPDFBlob(htmlAta(ata, nomeCliente), tituloDoc);
    const url = URL.createObjectURL(pdfBlob);
    const a = document.createElement('a');
    a.href = url; a.download = `${tituloDoc}.pdf`; a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    console.warn('Falha ao gerar PDF:', e);
  }

  // Variáveis de texto simples — o design fica inteiramente no template do EmailJS
  const participantesLista = asArray(ata.participantes)
    .filter(p => p.nome)
    .map((p, i) => `${i+1}. ${p.nome}${p.cargo ? ' — ' + p.cargo : ''}`)
    .join('\n') || '—';

  const pautasLista = (ata.pautas || [])
    .map((p, i) => {
      const prazoFmt = p.prazo ? new Date(p.prazo+'T00:00').toLocaleDateString('pt-BR') : '';
      return [
        `${i+1}. ${p.titulo || 'Sem título'}`,
        p.deliberacao  ? `   → ${p.deliberacao}` : '',
        p.responsaveis ? `   Responsável: ${p.responsaveis}` : '',
        prazoFmt       ? `   Prazo: ${prazoFmt}` : '',
      ].filter(Boolean).join('\n');
    })
    .join('\n\n') || 'Nenhuma pauta registrada.';

  const dataEmissao = new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });

  try {
    await emailjs.send(
      config.serviceId,
      config.templateIdAta || config.templateId,
      {
        assunto,
        nome_cliente:       nomeCliente || '—',
        data_reuniao:       dataFmt,
        horario:            `${ata.hora||'—'} às ${ata.horaFim||'—'}`,
        local:              ata.local || '—',
        redator:            ata.redator || '—',
        participantes_lista: participantesLista,
        pautas_lista:       pautasLista,
        data_emissao:       dataEmissao,
        destinatario:       destinatarios,
      },
      { publicKey: config.publicKey }
    );
    return { ok: true };
  } catch (err) {
    return { ok: false, erro: err?.text || err?.message || 'Erro desconhecido.' };
  }
}

const lerArquivo = (file) => new Promise((resolve) => {
  const reader = new FileReader();
  reader.onload = (e) => resolve({ nome: file.name, dados: e.target.result });
  reader.readAsDataURL(file);
});

export const GerenciadorAtas = ({ atas, setAtas, clientes, profissionais, profissionalAtual, isAdmin }) => {
  const [novaAta, setNovaAta]       = useState(null);
  const [editandoAta, setEditandoAta] = useState(null);
  const [expandidas, setExpandidas]  = useState({});
  const [filtroCliente, setFiltroCliente] = useState('');
  const [enviandoEmail, setEnviandoEmail] = useState(null); // id da ata em envio

  const criarAta = () => {
    setNovaAta({
      id: Date.now(),
      data: new Date().toISOString().split('T')[0],
      hora: '09:00',
      horaFim: '10:00',
      local: '',
      clienteId: '',
      redator: profissionalAtual || '',
      participantes: [{ nome: '', cargo: '' }],
      pautas: [],
      emailsDestinatarios: '',
      anexos: [],
    });
    setEditandoAta(null);
  };

  const addParticipante = () => setNovaAta(a => ({ ...a, participantes: [...asArray(a.participantes), { nome: '', cargo: '' }] }));
  const updParticipante = (i, campo, val) => setNovaAta(a => ({ ...a, participantes: asArray(a.participantes).map((p, idx) => idx === i ? { ...p, [campo]: val } : p) }));
  const remParticipante = (i) => setNovaAta(a => ({ ...a, participantes: asArray(a.participantes).filter((_, idx) => idx !== i) }));

  const handleEnviarEmail = async (ata) => {
    setEnviandoEmail(ata.id);
    const cliente = clientes.find(c => String(c.id) === String(ata.clienteId));
    const resultado = await enviarAtaPorEmail(ata, cliente?.nome);
    setEnviandoEmail(null);
    if (resultado.ok) toast.sucesso('E-mail enviado! PDF da ata baixado automaticamente.');
    else toast.erro('Erro ao enviar: ' + resultado.erro);
  };

  const adicionarPauta = () => {
    if (!novaAta) return;
    setNovaAta({ ...novaAta, pautas: [...(novaAta.pautas || []), { id: Date.now(), titulo: '', deliberacao: '', responsaveis: '', prazo: '' }] });
  };

  const atualizarPauta = (pautaId, campo, valor) => {
    if (!novaAta) return;
    setNovaAta({ ...novaAta, pautas: (novaAta.pautas || []).map(p => p.id === pautaId ? { ...p, [campo]: valor } : p) });
  };

  const removerPauta = (pautaId) => {
    if (!novaAta) return;
    setNovaAta({ ...novaAta, pautas: (novaAta.pautas || []).filter(p => p.id !== pautaId) });
  };

  const salvarAta = () => {
    if (!novaAta) return;
    const atualizado = editandoAta
      ? atas.map(a => a.id === editandoAta.id ? novaAta : a)
      : [...atas, novaAta];
    setAtas(atualizado);
    toast.sucesso(editandoAta ? 'Ata atualizada!' : 'Ata salva com sucesso!');
    setNovaAta(null);
    setEditandoAta(null);
  };

  const editarAta = (ata) => {
    // Migra campo profissional → redator para atas antigas
    const ataMigrada = {
      ...ata,
      redator: ata.redator || ata.profissional || '',
      participantes: Array.isArray(ata.participantes) && ata.participantes.length > 0
        ? ata.participantes
        : [{ nome: '', cargo: '' }],
    };
    setNovaAta(ataMigrada);
    setEditandoAta(ata);
  };

  const deletarAta = (id) => {
    if (window.confirm('Excluir esta ata?')) { setAtas(atas.filter(a => a.id !== id)); toast.sucesso('Ata excluída.'); }
  };

  const atasBase = isAdmin ? atas : atas.filter(a => !a.redator || a.redator === profissionalAtual);
  const atasFiltradas = filtroCliente ? atasBase.filter(a => String(a.clienteId) === String(filtroCliente)) : atasBase;

  const toggleExpandir = (ataId) => setExpandidas(prev => ({ ...prev, [ataId]: !prev[ataId] }));

  return (
    <div style={s.container}>
      <h2 style={s.titulo}>Atas de Reunião</h2>

      {/* Filtros + botão */}
      <div style={s.toolbar}>
        <div style={s.filtroGrupo}>
          <label style={s.filtroLabel}>Filtrar por Cliente</label>
          <select style={s.select} value={filtroCliente} onChange={e => setFiltroCliente(e.target.value)}>
            <option value="">Todas as Atas</option>
            {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
        </div>
        {!novaAta && (
          <button style={s.btnNovo} onClick={criarAta}><Plus size={16}/> Nova Ata</button>
        )}
      </div>

      {/* Formulário */}
      {novaAta && (
        <div style={s.formCard}>
          <h3 style={s.formTitulo}>{editandoAta ? '✏️ Editar Ata' : '📋 Nova Ata'}</h3>

          <div style={s.formRow}>
            <div style={s.campo}>
              <label style={s.label}>Data</label>
              <input type="date" style={s.input} value={novaAta.data} onChange={e => setNovaAta({...novaAta, data: e.target.value})} />
            </div>
            <div style={s.campo}>
              <label style={s.label}>Hora Início</label>
              <input type="time" style={s.input} value={novaAta.hora} onChange={e => setNovaAta({...novaAta, hora: e.target.value})} />
            </div>
            <div style={s.campo}>
              <label style={s.label}>Hora Fim</label>
              <input type="time" style={s.input} value={novaAta.horaFim || ''} onChange={e => setNovaAta({...novaAta, horaFim: e.target.value})} />
            </div>
          </div>

          <div style={s.formRow}>
            <div style={s.campo}>
              <label style={s.label}>Local</label>
              <input type="text" style={s.input} value={novaAta.local} placeholder="Ex: Sala de Reuniões / Online" onChange={e => setNovaAta({...novaAta, local: e.target.value})} />
            </div>
            <div style={s.campo}>
              <label style={s.label}>Cliente</label>
              <select style={s.input} value={novaAta.clienteId} onChange={e => setNovaAta({...novaAta, clienteId: e.target.value})}>
                <option value="">Selecione um cliente</option>
                {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </div>
          </div>

          {/* Pautas */}
          <div style={s.pautasSecao}>
            <div style={s.pautasHeader}>
              <span style={s.pautasTitulo}>Pautas e Deliberações</span>
              <button style={s.btnAddPauta} onClick={adicionarPauta}><Plus size={14}/> Adicionar Pauta</button>
            </div>

            {(novaAta.pautas || []).length === 0 && (
              <p style={s.pautasVazio}>Nenhuma pauta adicionada. Clique em "Adicionar Pauta" para começar.</p>
            )}

            {(novaAta.pautas || []).map((pauta, idx) => (
              <div key={pauta.id} style={s.pautaCard}>
                <div style={s.pautaNumero}>Pauta {idx + 1}</div>
                <div style={s.campo}>
                  <label style={s.label}>Título da Pauta</label>
                  <input type="text" style={s.input} value={pauta.titulo} placeholder="Assunto a ser discutido" onChange={e => atualizarPauta(pauta.id, 'titulo', e.target.value)} />
                </div>
                <div style={s.campo}>
                  <label style={s.label}>Deliberação</label>
                  <textarea style={{...s.input, resize:'vertical', minHeight:60}} value={pauta.deliberacao} placeholder="Decisão tomada, encaminhamento..." onChange={e => atualizarPauta(pauta.id, 'deliberacao', e.target.value)} rows={2} />
                </div>
                <div style={s.formRow}>
                  <div style={s.campo}>
                    <label style={s.label}>Responsável(is)</label>
                    <input type="text" style={s.input} value={pauta.responsaveis} placeholder="Nomes dos responsáveis" onChange={e => atualizarPauta(pauta.id, 'responsaveis', e.target.value)} />
                  </div>
                  <div style={s.campo}>
                    <label style={s.label}>Prazo</label>
                    <input type="date" style={s.input} value={pauta.prazo} onChange={e => atualizarPauta(pauta.id, 'prazo', e.target.value)} />
                  </div>
                  <button style={s.btnRemPauta} onClick={() => removerPauta(pauta.id)}><Trash2 size={15}/></button>
                </div>
              </div>
            ))}
          </div>

          {/* Participantes */}
          <div style={s.pautasSecao}>
            <div style={s.pautasHeader}>
              <span style={s.pautasTitulo}><Users size={13} style={{marginRight:5}}/>Participantes</span>
              <button style={s.btnAddPauta} onClick={addParticipante}><Plus size={14}/> Adicionar</button>
            </div>
            {asArray(novaAta.participantes).length === 0 && (
              <p style={s.pautasVazio}>Nenhum participante adicionado.</p>
            )}
            {asArray(novaAta.participantes).map((p, i) => (
              <div key={i} style={{ display:'flex', gap:8, alignItems:'center' }}>
                <input type="text" style={{ ...s.input, flex:2 }} value={p.nome||''} placeholder="Nome do participante" onChange={e => updParticipante(i, 'nome', e.target.value)} />
                <input type="text" style={{ ...s.input, flex:1 }} value={p.cargo||''} placeholder="Cargo / Empresa" onChange={e => updParticipante(i, 'cargo', e.target.value)} />
                <button style={s.btnRemPauta} onClick={() => remParticipante(i)}><Trash2 size={13}/></button>
              </div>
            ))}
          </div>

          <div style={s.campo}>
            <label style={s.label}>Redator(a)</label>
            <input type="text" style={s.input} value={novaAta.redator||''} placeholder="Nome do redator(a) da ata" onChange={e => setNovaAta({...novaAta, redator:e.target.value})} />
          </div>
          <div style={s.campo}>
            <label style={s.label}>E-mails Destinatários <span style={{fontWeight:400,textTransform:'none',color:'#64748b'}}>(separados por vírgula)</span></label>
            <input type="text" style={s.input} value={novaAta.emailsDestinatarios} placeholder="email1@exemplo.com, email2@exemplo.com" onChange={e => setNovaAta({...novaAta, emailsDestinatarios: e.target.value})} />
          </div>

          <div style={s.campo}>
            <label style={s.label}>Anexos (PDF / Ata Assinada)</label>
            <div style={s.anexoRow}>
              <label style={s.btnAnexar}>
                <Paperclip size={14}/> Adicionar arquivo(s)
                <input type="file" accept=".pdf,.doc,.docx,.png,.jpg" multiple style={{display:'none'}} onChange={async (e) => {
                  const files = Array.from(e.target.files);
                  if (!files.length) return;
                  const resultados = await Promise.all(files.map(lerArquivo));
                  setNovaAta(a => ({ ...a, anexos: [...(a.anexos||[]), ...resultados] }));
                  e.target.value = '';
                }} />
              </label>
            </div>
            {(novaAta.anexos||[]).length > 0 && (
              <div style={s.anexoLista}>
                {(novaAta.anexos||[]).map((a, i) => (
                  <span key={i} style={s.anexoChip}>
                    <FileText size={12}/> {a.nome}
                    <button style={s.btnRemAnexo} onClick={() => setNovaAta(at => ({ ...at, anexos: (at.anexos||[]).filter((_,j)=>j!==i) }))}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div style={s.formActions}>
            <button style={s.btnSalvar} onClick={salvarAta}>Salvar Ata</button>
            <button style={s.btnCancelar} onClick={() => { setNovaAta(null); setEditandoAta(null); }}>Cancelar</button>
          </div>
        </div>
      )}

      {/* Lista */}
      {atasFiltradas.length === 0
        ? <div style={s.vazio}><FileText size={40} color="#e5e0ef" /><p>Nenhuma ata registrada.</p></div>
        : atasFiltradas.map(ata => {
            const cliente  = clientes.find(c => String(c.id) === String(ata.clienteId));
            const expanded = expandidas[ata.id];
            return (
              <div key={ata.id} style={s.ataCard}>
                <div style={s.ataHeader} onClick={() => toggleExpandir(ata.id)}>
                  <div style={s.ataInfo}>
                    <div style={s.ataCliente}>{cliente?.nome || 'Cliente não definido'}</div>
                    <div style={s.ataMeta}>
                      {ata.data ? new Date(ata.data+'T00:00').toLocaleDateString('pt-BR') : '—'}
                      {' · '}{ata.hora}{ata.horaFim ? ` às ${ata.horaFim}` : ''}
                      {ata.local ? ` · ${ata.local}` : ''}
                      {' · '}<span style={s.pautasBadge}>{(ata.pautas||[]).length} pauta(s)</span>
                    </div>
                  </div>
                  <div style={s.ataAcoesRight}>
                    <button style={s.btnExport} onClick={e => { e.stopPropagation(); exportarPDF(htmlAta(ata, cliente?.nome), `Ata - ${cliente?.nome || 'Reunião'}`); }} title="Exportar PDF">PDF</button>
                    <button style={s.btnExport} onClick={e => { e.stopPropagation(); exportarWord(htmlAta(ata, cliente?.nome), `Ata - ${cliente?.nome || 'Reunião'}`); }} title="Exportar Word">DOC</button>
                    <button
                      style={{ ...s.btnExport, display:'flex', alignItems:'center', gap:4, color: enviandoEmail === ata.id ? '#64748b' : '#1d4ed8', borderColor: enviandoEmail === ata.id ? '#e5e0ef' : 'rgba(56,139,253,0.4)' }}
                      onClick={e => { e.stopPropagation(); handleEnviarEmail(ata); }}
                      title="Enviar ata por e-mail"
                      disabled={enviandoEmail === ata.id}
                    >
                      <Send size={11}/> {enviandoEmail === ata.id ? '...' : 'Email'}
                    </button>
                    <button style={s.btnEdit} onClick={e => { e.stopPropagation(); editarAta(ata); }}><Edit2 size={15}/></button>
                    <button style={s.btnDel} onClick={e => { e.stopPropagation(); deletarAta(ata.id); }}><Trash2 size={15}/></button>
                    {expanded ? <ChevronUp size={18} color="#5f5874"/> : <ChevronDown size={18} color="#5f5874"/>}
                  </div>
                </div>

                {expanded && (
                  <div style={s.ataConteudo}>
                    {(ata.pautas || []).length === 0
                      ? <p style={{color:'#5f5874',fontSize:13}}>Nenhuma pauta registrada.</p>
                      : (ata.pautas || []).map((pauta, idx) => (
                          <div key={pauta.id} style={s.pautaResumo}>
                            <div style={s.pautaResumoNum}>{idx + 1}</div>
                            <div style={{flex:1}}>
                              <div style={s.pautaResumoTitulo}>{pauta.titulo || 'Sem título'}</div>
                              {pauta.deliberacao && <div style={s.pautaResumoField}><strong>Deliberação:</strong> {pauta.deliberacao}</div>}
                              {pauta.responsaveis && <div style={s.pautaResumoField}><strong>Responsável(is):</strong> {pauta.responsaveis}</div>}
                              {pauta.prazo && <div style={s.pautaResumoField}><strong>Prazo:</strong> {new Date(pauta.prazo+'T00:00').toLocaleDateString('pt-BR')}</div>}
                            </div>
                          </div>
                        ))
                    }
                    {/* Participantes */}
                    {asArray(ata.participantes).filter(p => p.nome).length > 0 && (
                      <div style={{ fontSize:13, color:'#5f5874' }}>
                        <strong style={{ color:'#2d2640' }}>Participantes:</strong>{' '}
                        {asArray(ata.participantes).filter(p => p.nome).map(p => p.cargo ? `${p.nome} (${p.cargo})` : p.nome).join(' · ')}
                      </div>
                    )}
                    {ata.redator && (
                      <div style={{ fontSize:13, color:'#5f5874' }}><strong style={{ color:'#2d2640' }}>Redator(a):</strong> {ata.redator}</div>
                    )}
                    {ata.emailsDestinatarios && (
                      <div style={s.emailsRow}><strong>Destinatários:</strong> {ata.emailsDestinatarios}</div>
                    )}
                    {(ata.anexos||[]).length > 0 && (
                      <div style={s.anexoLista}>
                        {(ata.anexos||[]).map((a, i) => (
                          <button key={i} style={s.anexoChipDown}
                            onClick={() => { const link=document.createElement('a'); link.href=a.dados; link.download=a.nome; link.click(); }}>
                            <Download size={12}/> {a.nome}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
      }
    </div>
  );
};

const s = {
  container:      { display:'flex', flexDirection:'column', gap:'1.25rem' },
  titulo:         { fontSize:'1.8rem', fontWeight:700, color:'#1c1530' },
  toolbar:        { display:'flex', justifyContent:'space-between', alignItems:'flex-end', flexWrap:'wrap', gap:'1rem', background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:12, padding:'1.25rem' },
  filtroGrupo:    { display:'flex', flexDirection:'column', gap:4 },
  filtroLabel:    { fontSize:12, color:'#5f5874', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' },
  select:         { background:'#f7f5fb', border:'1px solid #e5e0ef', borderRadius:8, color:'#1c1530', padding:'8px 12px', fontSize:13, minWidth:200 },
  btnNovo:        { display:'flex', alignItems:'center', gap:6, background:'#7c3aed', color:'white', border:'none', borderRadius:20, padding:'9px 20px', fontSize:13, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' },
  formCard:       { background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:12, padding:'1.5rem', display:'flex', flexDirection:'column', gap:'1rem' },
  formTitulo:     { fontSize:'1.1rem', fontWeight:700, color:'#1c1530', margin:0 },
  formRow:        { display:'flex', gap:'1rem', flexWrap:'wrap', alignItems:'flex-end' },
  campo:          { display:'flex', flexDirection:'column', gap:5, flex:1, minWidth:140 },
  label:          { fontSize:12, fontWeight:600, color:'#5f5874', textTransform:'uppercase', letterSpacing:'0.04em' },
  input:          { background:'#f7f5fb', border:'1px solid #e5e0ef', borderRadius:8, color:'#1c1530', padding:'8px 12px', fontSize:13, outline:'none', width:'100%', boxSizing:'border-box' },
  pautasSecao:    { background:'#ffffff', border:'1px solid #eeeaf5', borderRadius:10, padding:'1rem', display:'flex', flexDirection:'column', gap:'0.75rem' },
  pautasHeader:   { display:'flex', justifyContent:'space-between', alignItems:'center' },
  pautasTitulo:   { fontSize:13, fontWeight:700, color:'#1c1530', textTransform:'uppercase', letterSpacing:'0.05em' },
  btnAddPauta:    { display:'flex', alignItems:'center', gap:5, background:'transparent', border:'1px solid #7c3aed', color:'#8b5cf6', borderRadius:8, padding:'6px 12px', fontSize:12, fontWeight:600, cursor:'pointer' },
  pautasVazio:    { fontSize:13, color:'#5f5874', textAlign:'center', padding:'0.5rem 0' },
  pautaCard:      { background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:8, padding:'1rem', display:'flex', flexDirection:'column', gap:'0.75rem' },
  pautaNumero:    { fontSize:11, fontWeight:700, color:'#7c3aed', textTransform:'uppercase', letterSpacing:'0.08em' },
  btnRemPauta:    { background:'transparent', border:'1px solid #ef4444', color:'#ef4444', borderRadius:8, padding:'8px', cursor:'pointer', display:'flex', alignItems:'center', alignSelf:'flex-end' },
  formActions:    { display:'flex', gap:'0.75rem', justifyContent:'flex-end' },
  btnSalvar:      { background:'#7c3aed', border:'none', borderRadius:8, color:'white', cursor:'pointer', padding:'9px 22px', fontSize:13, fontWeight:600 },
  btnCancelar:    { background:'transparent', border:'1px solid #e5e0ef', borderRadius:8, color:'#5f5874', cursor:'pointer', padding:'9px 16px', fontSize:13 },
  vazio:          { display:'flex', flexDirection:'column', alignItems:'center', gap:'0.75rem', padding:'3rem', color:'#5f5874', fontSize:14 },
  ataCard:        { background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:12, overflow:'hidden' },
  ataHeader:      { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'1.1rem 1.25rem', cursor:'pointer' },
  ataInfo:        { display:'flex', flexDirection:'column', gap:4 },
  ataCliente:     { fontSize:15, fontWeight:700, color:'#1c1530' },
  ataMeta:        { fontSize:12, color:'#5f5874' },
  pautasBadge:    { background:'rgba(124,58,237,0.15)', color:'#8b5cf6', borderRadius:10, padding:'1px 7px', fontSize:11, fontWeight:600 },
  ataAcoesRight:  { display:'flex', alignItems:'center', gap:6 },
  btnExport:      { background:'transparent', border:'1px solid #e5e0ef', borderRadius:6, color:'#5f5874', cursor:'pointer', padding:'3px 7px', fontSize:11, fontWeight:600 },
  btnEdit:        { background:'transparent', border:'none', color:'#8b5cf6', cursor:'pointer', padding:4, display:'flex' },
  btnDel:         { background:'transparent', border:'none', color:'#ef4444', cursor:'pointer', padding:4, display:'flex' },
  ataConteudo:    { borderTop:'1px solid #eeeaf5', padding:'1.25rem', display:'flex', flexDirection:'column', gap:'0.75rem' },
  pautaResumo:    { display:'flex', gap:'0.75rem', background:'#f7f5fb', borderRadius:8, padding:'0.875rem' },
  pautaResumoNum: { width:24, height:24, background:'#7c3aed', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, color:'white', flexShrink:0 },
  pautaResumoTitulo: { fontSize:14, fontWeight:600, color:'#1c1530', marginBottom:4 },
  pautaResumoField:  { fontSize:13, color:'#5f5874', marginTop:2, whiteSpace:'pre-wrap' },
  emailsRow:      { fontSize:13, color:'#5f5874', paddingTop:'0.5rem', borderTop:'1px solid #eeeaf5' },
  anexoRow:       { display:'flex', alignItems:'center', gap:'0.75rem', flexWrap:'wrap' },
  btnAnexar:      { display:'flex', alignItems:'center', gap:6, background:'transparent', border:'1px dashed #e5e0ef', borderRadius:8, color:'#5f5874', padding:'8px 14px', fontSize:12, cursor:'pointer' },
  anexoLista:     { display:'flex', flexWrap:'wrap', gap:6, marginTop:4 },
  anexoChip:      { display:'flex', alignItems:'center', gap:5, fontSize:11, color:'#8b5cf6', background:'rgba(139,92,246,0.1)', border:'1px solid rgba(139,92,246,0.3)', borderRadius:20, padding:'3px 10px' },
  btnRemAnexo:    { background:'transparent', border:'none', color:'#8b5cf6', cursor:'pointer', fontSize:14, lineHeight:1, padding:'0 2px', fontWeight:700 },
  anexoChipDown:  { display:'flex', alignItems:'center', gap:5, fontSize:11, color:'#8b5cf6', background:'rgba(139,92,246,0.1)', border:'1px solid rgba(139,92,246,0.3)', borderRadius:20, padding:'4px 10px', cursor:'pointer' },
};
